import * as React from "preact";
import cx from "classnames";
import Cookies from "js-cookie";
import { Fragment, FunctionComponent } from "preact";
import { useEffect, useState } from "preact/hooks";
import sanitizeHtml from "sanitize-html";

import utils from "../../helpers/utils";
import fauxDropdown from "../../modules/faux-dropdown";

interface IAppProps {
  apiUrl: string;
  initialRegion?: string;
  regionText: string;
  regionSelectLabel: string;
  initialRole?: string;
  roleText: string;
  roleSelectLabel: string;
  ctaText: string;
  cancelCtaText: string;
  isSingleRegion: boolean;
  cookieText?: string;
  cookieAcceptBtnLabel?: string;
  cookieEditBtnLabel?: string;
  cookieRejectAllBtnLabel?: string;
  enableCookieScript?: boolean;
}

const App: FunctionComponent<IAppProps> = ({
  apiUrl,
  initialRegion,
  regionText,
  regionSelectLabel,
  initialRole,
  roleText,
  roleSelectLabel,
  ctaText,
  cancelCtaText,
  isSingleRegion,
  cookieText,
  cookieAcceptBtnLabel,
  cookieEditBtnLabel,
  cookieRejectAllBtnLabel,
  enableCookieScript,
}) => {
  // When changing a role / region from outside the app - retrieve the newly clicked value from localStorage (regions-roles-modal-toggle.ts)
  // Otherwise, use current region & role if passed
  const initialRegionId =
    localStorage.getItem("newRegionId") || initialRegion || utils.getUrlParameter("Region") || Cookies.get("Region");
  const initialRoleId =
    localStorage.getItem("newRoleId") || initialRole || utils.getUrlParameter("Role") || Cookies.get("Role");
  const [currentRegion, setCurrentRegion] = useState(initialRegionId);
  const [currentRole, setCurrentRole] = useState(initialRoleId);
  const [data, setData] = useState<any>(null);
  const [roles, setRoles] = useState<any[]>([]);
  const [regions, setRegions] = useState<any[]>([]);
  const [disabled, setDisabled] = useState(true);
  // Remove temporary values as no longer needed
  localStorage.removeItem("newRoleId");
  localStorage.removeItem("newRegionId");
  const alreadyAccepted = utils.convertToBoolean(Cookies.get("SavedCivicCookies"));
  const canAllowAll = !!window.CookieControl && !!window.CookieControl.acceptAll;
  const canRejectAll = !!window.CookieControl && !!window.CookieControl.rejectAll;
  const showCookies =
    enableCookieScript && (cookieAcceptBtnLabel || cookieEditBtnLabel || cookieRejectAllBtnLabel) && !alreadyAccepted;
  const isInCurrentRegion = (role) =>
    role.Regions.some((region) => region.Id === currentRegion);

  // Sets initial lists
  useEffect(() => {
    const fetchData = async () => {
      try {
        const pageLink = encodeURIComponent(window.location.origin + window.location.pathname + window.location.search);
        const res = await fetch(apiUrl + "?pageLink=" + pageLink);
        const data = await res.json();
        const userRoles = data ? data.UserRoles : null;
        if (userRoles && userRoles.length) {
          // Filter out roles not matching a region if only initial region exists (it can happen with a direct link)
          if (!currentRole && currentRegion) {

            const filteredRoles = userRoles.filter(isInCurrentRegion);
            setRoles(filteredRoles);
          } else {
            setRoles(userRoles);
          }
          setData(data);
          setRolesAndRegions(userRoles);
          setTimeout(() => fauxDropdown.init()); // Adds interactivity & accessibility
        }
      } catch(error) {
        console.log("1. error fetching or showing roles & regions data: ", error)
      }
    }
    fetchData();
  }, [apiUrl]);

  // Sets lists on subsequent role / region changes
  useEffect(() => {
    if (data) {
      const userRoles = data?.roles;
      if (userRoles && userRoles.length) {
        setRoles(userRoles);
        setRolesAndRegions(userRoles);
      }
    }

    // Region & role changes update the lists - it happens in setRolesAndRegions, hence dependencies
  }, [data, currentRegion, currentRole]);

  useEffect(() => {
    if (isSingleRegion && !!regions && !!regions.length && regions.length === 1) {
      setCurrentRegion(regions[0]?.Id);
    }
  }, [regions, isSingleRegion]);

  useEffect(() => {
    const invalidRoleRegion = (!currentRegion && !isSingleRegion) || !currentRole;
    setDisabled(invalidRoleRegion);
  }, [currentRegion, isSingleRegion, currentRole]);

  const setRolesAndRegions = (userRoles) => {
    if (!currentRole) {
      // Join all the regions from all the roles into a single array
      const allRegions = userRoles
        .reduce((acc, r) => acc.concat(r.Regions), [])
        // Eliminate dupes
        .filter((item, i, arr) => arr.findIndex((r) => r.Id === item.Id) === i); // Boom
      setRegions(allRegions);
    }
    if (currentRole) {
      const role = userRoles.find((r) => r.RoleId === currentRole);
      if (role) {
        setRegions(role.Regions);
      }
    }
  };

  const handleRoleChange = (id) => {
    // Update the list of regions to only those matching the selected role
    // Making sure we filter the initial list & not the potentially already filtered list
    const selectedRole = data?.UserRoles.find((role) => role.RoleId === id);
    if (selectedRole) {
      setRegions(selectedRole.Regions);
    }
    setCurrentRole(id);
  };

  const handleRegionChange = (id) => {
    const rolesMatchingRegion = data?.UserRoles?.filter((item) => {
      return item.Regions.findIndex((reg) => reg.Id === id) !== -1;
    });
    // Update the list of roles to only those matching the selected region
    // Making sure we filter the initial list & not the potentially already filtered list
    setRoles(rolesMatchingRegion);
    setCurrentRegion(id);
  };

  const handleCancel = (roleId, regionId) => () => {
    setCurrentRole(roleId);
    setCurrentRegion(regionId);
    location.reload();
  };

  const handleSubmit =
    (acceptedAllCookies = false, editCookies = false, rejectAll = false) =>
    () => {
      utils.setCookie("SeenCookieMessage", true, 365);
      if (acceptedAllCookies && canAllowAll) {
        window.CookieControl.acceptAll(); // Accept all if user clicks on "Agree and accept all cookies"
        utils.setCookie("SavedCivicCookies", true, 365); // Do not show this message on subsequent role & region prompt show
      }
      if (!!currentRegion && !!currentRole) {
        const fullRegion = !!regions && regions.find((r) => r?.Id === currentRegion);
        utils.setCookie("Region", currentRegion, 365);
        utils.setCookie("Role", currentRole, 365);
        if (editCookies) {
          utils.setCookie("ShowCookieHandlerOnNextLoad", true, 1);
          utils.setCookie("SavedCivicCookies", false, 365);
        }
        if (canRejectAll && rejectAll) {
          window.CookieControl.rejectAll();
          utils.setCookie("ShowCookieHandlerOnNextLoad", false, 365);
          utils.setCookie("SavedCivicCookies", true, 365);
        }
        window.location.href = fullRegion?.RegionUrl + window.location.hash;
      }
    };

  if ((!roles?.length) || !data) {
    return (
      <div className="modal__loading-area">
        <div className="loading-indicator loading-indicator--central"></div>
      </div>
    );
  } // Don't bother until we have data
  const declarationsText = sanitizeHtml(data?.Declarations, {
    // Allow potentially more tags than we need right now, as this might be a bit more in the future (i.e. cookie message might contain a table etc)
    nonTextTags: ["style", "script", "textarea", "option", "xmp"],
    allowedTags: [
      "h3",
      "h4",
      "h5",
      "h6",
      "p",
      "a",
      "ul",
      "ol",
      "li",
      "b",
      "i",
      "strong",
      "em",
      "table",
      "thead",
      "caption",
      "tbody",
      "tr",
      "th",
      "td",
      "img",
    ],
  });
  return (
    <Fragment>
      <div className="modal__sub-header">
        <div className="border inline-container inline-container--space">
          <Regions
            currentRegion={currentRegion}
            regions={regions}
            regionText={regionText}
            defaultOptionText={regionSelectLabel}
            handleRegionChange={handleRegionChange}
            isSingleRegion={isSingleRegion}
          />
          <Roles
            currentRole={currentRole}
            roles={roles}
            roleText={roleText}
            defaultOptionText={roleSelectLabel}
            handleRoleChange={handleRoleChange}
          />
        </div>
      </div>

      <div className="modal__section modal__section--min-height richtext-content">
        {data.Declarations && <div dangerouslySetInnerHTML={{ __html: declarationsText }} />}
        {showCookies && cookieText && <div dangerouslySetInnerHTML={{ __html: cookieText }} />}
      </div>

      <footer
        className={cx(
          "modal__footer modal__section modal__footer--with-divider modal__footer--padding modal__footer--with-cta",
          {
            "modal__footer--two-btns":
              showCookies && cookieEditBtnLabel && cookieAcceptBtnLabel && cookieRejectAllBtnLabel,
          }
        )}
      >
        {showCookies && cookieAcceptBtnLabel ? (
          <SubmitButton
            ctaText={cookieAcceptBtnLabel}
            disabled={disabled}
            className="cta--highlight cta--with-arrow"
            onClick={handleSubmit(true)}
          />
        ) : (
          <Fragment>
            <SubmitButton
              ctaText={ctaText}
              disabled={disabled}
              className="cta--highlight cta--with-arrow"
              onClick={handleSubmit(true)}
            />
            <CancelButton
              ctaText={cancelCtaText}
              disabled={disabled}
              className="js-modal-close cta cta--link cta--link-left"
              onClick={handleCancel(initialRoleId, initialRegionId)}
            />
          </Fragment>
        )}

        {showCookies && cookieRejectAllBtnLabel && (
          <SubmitButton
            ctaText={cookieRejectAllBtnLabel}
            disabled={disabled}
            className="cta--highlight cta--with-arrow"
            onClick={handleSubmit(false, false, true)}
          />
        )}

        {showCookies && cookieEditBtnLabel && (
          <SubmitButton
            ctaText={cookieEditBtnLabel}
            disabled={disabled}
            className="cta--highlight cta--with-arrow"
            onClick={handleSubmit(false, true)}
          />
        )}
      </footer>
    </Fragment>
  );
};
export default App;

// <Regions>
interface IRegionsProps {
  currentRegion: string;
  regions: any[];
  regionText: string;
  defaultOptionText: string;
  isSingleRegion: boolean;
  handleRegionChange(id: string): void;
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Regions: FunctionComponent<IRegionsProps> = ({
  currentRegion,
  regions,
  regionText,
  defaultOptionText,
  isSingleRegion,
  handleRegionChange,
}) => {
  if (!regions || !regions.length || isSingleRegion) {
    return null;
  }
  const handleChange = (event) => handleRegionChange(event.target.value);
  const handleDropdownClick = (id) => (e) => {
    handleRegionChange(id);
    if (e) {
      e.preventDefault();
    }
  };
  const currentFullRegion = regions.find((r) => r.Id === currentRegion);
  const dropdownText = currentRegion && currentFullRegion ? currentFullRegion.RegionName : defaultOptionText;

  return (
    <div className="inline-container__inline-section">
      <span className="inline-container__inline-text">{regionText}</span>
      {/* Mobile select */}
      <select
        className="inline-container__select inline-container__select--margin"
        onChange={handleChange}
        value={currentRegion || ""}
      >
        <option
          value=""
          disabled
        >
          {defaultOptionText}
        </option>
        {regions.map((region) => (
          <option
            value={region.Id}
            key={region.Id}
          >
            {region.RegionName}
          </option>
        ))}
      </select>
      {/* Faux select on desktop */}
      <div className="faux-dropdown inline-container__faux-dropdown inline-container__faux-dropdown--inline">
        <button className="inline-container__faux-select inline-container__trigger faux-dropdown__trigger js-faux-dropdown">
          {dropdownText}
        </button>
        <ul
          className="faux-dropdown__menu inline-container__faux-dropdown-menu js-faux-dropdown-menu"
          aria-label="Please select"
          aria-activedescendant={currentRegion && `region-select-list-hat-app-${currentRegion}`}
        >
          {regions.map((r) => {
            const rId = r.Id;
            return (
            <li
                className="faux-dropdown__menu-item"
                onClick={handleDropdownClick(rId)}
                onMouseDown={handleDropdownClick(rId)}
                onKeyDown={handleDropdownClick(rId)}
                key={rId}
              >
                <button
                  className={cx("unbutton faux-dropdown__menu-item-link", "js-faux-item-link", {
                    "faux-dropdown__menu-item-link--with-ticks selected": rId === currentRegion,
                  })}
                  id={`region-select-list-hat-app-${rId}`}
                >
                  <span className="faux-dropdown__menu-item-link-text">{r.RegionName}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

// <Roles>
interface IRolesProps {
  currentRole: string;
  roles: any[];
  roleText: string;
  defaultOptionText: string;
  handleRoleChange(id: string): void;
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Roles: FunctionComponent<IRolesProps> = ({
  currentRole,
  roles,
  roleText,
  defaultOptionText,
  handleRoleChange,
}) => {
  if (!roles || !roles.length) {
    return null;
  }
  const handleChange = (event) => handleRoleChange(event.target.value);
  const handleDropdownClick = (id) => (e) => {
    handleRoleChange(id);
    if (e) {
      e.preventDefault();
    }
  };
  const currentFullRole = roles.find((r) => r.RoleId === currentRole);
  const dropdownText = currentRole && currentFullRole ? currentFullRole.RoleName : defaultOptionText;

  return (
    <div className="inline-container__inline-section">
      <span className="inline-container__inline-text">{roleText}</span>
      {/* Mobile select */}
      <select
        className="inline-container__select inline-container__select--margin"
        onChange={handleChange}
        value={currentRole || ""}
      >
        <option
          value=""
          disabled
        >
          {defaultOptionText}
        </option>
        {roles.map((role) => (
          <option
            value={role.RoleId}
            key={role.RoleId}
          >
            {role.RoleName}
          </option>
        ))}
      </select>
      {/* Faux select on desktop */}
      <div className="faux-dropdown inline-container__faux-dropdown inline-container__faux-dropdown--inline">
        <button
          className={cx(
            "inline-container__faux-select inline-container__trigger faux-dropdown__trigger js-faux-dropdown"
          )}
        >
          {dropdownText}
        </button>
        <ul
          className="faux-dropdown__menu inline-container__faux-dropdown-menu js-faux-dropdown-menu"
          aria-label="Please select"
          aria-activedescendant={currentRole && `role-select-list-hat-app-${currentRole}`}
        >
          {roles.map((r) => {
            const rId = r.RoleId;
            return (
              <li
                className="faux-dropdown__menu-item"
                onClick={handleDropdownClick(rId)}
                onMouseDown={handleDropdownClick(rId)}
                key={rId}
              >
                <button
                  className={cx("unbutton faux-dropdown__menu-item-link", "js-faux-item-link", {
                    "faux-dropdown__menu-item-link--with-ticks selected": rId === currentRole,
                  })}
                  id={`role-select-list-hat-app-${rId}`}
                >
                  <span className="faux-dropdown__menu-item-link-text">{r.RoleName}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

// <SubmitButton>
interface ISubmitButtonProps {
  ctaText: string;
  id?: string;
  className?: string;
  disabled: boolean;
  onClick(): void;
}
// Technically <a> tag would be more appropriate, but disabled class comes almost out of the box with responsiveBP
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SubmitButton: FunctionComponent<ISubmitButtonProps> = ({ ctaText, disabled, onClick, className, id }) => (
  <button
    className={cx("cta", className, { disabled })}
    id={id}
    onClick={onClick}
  >
    {ctaText}
  </button>
);

interface ICancelButtonProps {
  ctaText: string;
  className?: string;
  disabled: boolean;
  onClick(): void;
}

const CancelButton: FunctionComponent<ICancelButtonProps> = ({ ctaText, className, disabled, onClick }) => (
  <button
    className={cx("unbutton", className, { disabled })}
    onClick={onClick}
  >
    {ctaText}
  </button>
);
